import { FC, useCallback } from 'react';
import { Button } from '@new/common/ui';
import { queryClient, teachurKeys, useStartQuizMutation } from '@teachur/http-client';
import { useSingleCourse } from '../../../../contexts/SingleCourseProvider';
import { useExamContext } from '../../../../contexts/ExamProvider';
import { useRequireSubscribed } from '@new/features/subscription/hooks/useRequireSubscribed';
import { toast } from 'react-toastify';

interface ExamActionCardProps {
  title: string;
  description: string;
  buttonText: string;
  backgroundColor: string;
  disabled?: boolean;
}

export const ExamActionCard: FC<ExamActionCardProps> = ({
  title,
  description,
  buttonText,
  backgroundColor,
  disabled,
}) => {
  const { course } = useSingleCourse();
  const { setExamMode } = useExamContext();
  const requireSubscribed = useRequireSubscribed();

  const startQuizMutation = useStartQuizMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teachurKeys.quizzes.list() });
      queryClient.invalidateQueries({ queryKey: teachurKeys.courses.completion(course?.id || 0) });
    },
  });

  const handleStartQuiz = useCallback(async () => {
    if (!course) return;

    try {
      const newQuiz = await startQuizMutation.mutateAsync(course.id);
      setExamMode({
        type: 'question',
        attempt: newQuiz,
      });
    } catch {
      toast.error('Failed to start quiz');
    }
  }, [course, setExamMode, startQuizMutation]);

  return (
    <div
      className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 rounded-lg ${backgroundColor}`}
    >
      <div className="flex flex-col">
        <span className="[font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p3)]! font-semibold text-(--neutral-black-NB800)!">
          {title}
        </span>
        <span className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB500)!">
          {description}
        </span>
      </div>

      <Button
        variant="primary"
        onClick={requireSubscribed(handleStartQuiz)}
        disabled={disabled || startQuizMutation.isPending}
      >
        {buttonText}
      </Button>
    </div>
  );
};
